"use client";

import { useEffect, useState } from "react";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { useRouter, useSearchParams } from "next/navigation";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/shared/data-table";
import { Loader } from "@/components/ui/loader";
import { TooltipProvider } from "@/components/ui/tooltip";
import TablePagination from "@/components/shared/table-pagination";
import { fetchData } from "@/utils/api-handler";
import {
  DEFAULT_TABLE_PAGE_LIMIT,
  DEFAULT_TABLE_SORT_BY_CREATED_AT,
  SEARCH_DEBOUNCE_WAIT,
} from "@/utils/global";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import {
  getScheduledJobsColumns,
  SCHEDULED_JOBS_API_PATH,
  SCHEDULED_JOB_EXECUTION_STATUS_OPTIONS,
  SCHEDULED_JOB_STATUS_OPTIONS,
  SCHEDULED_JOB_SORT_FIELDS,
  SCHEDULED_JOB_FILTER_ALL,
  type ScheduledJobSortField,
} from "@/components/scheduled-job";


const COMMAND_AGENT_ENTITY = "command_agent" as const;

type Props = {
  agentId: string;
};

function buildCommandAgentScheduledJobsUrl(params: {
  agentId: string;
  page: number;
  limit: number;
  sortBy: ScheduledJobSortField;
  sortOrder: "asc" | "desc";
  search: string;
  executionStatus: string;
  jobStatus: string;
}): string {
  const sp = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    sort_by: params.sortBy,
    sort_order: params.sortOrder,
    entity_type: COMMAND_AGENT_ENTITY,
    entity_id: params.agentId,
  });
  if (params.search.trim()) {
    sp.set("search", params.search.trim());
  }
  if (params.executionStatus !== SCHEDULED_JOB_FILTER_ALL) {
    sp.append("execution_status", params.executionStatus);
  }
  if (params.jobStatus !== SCHEDULED_JOB_FILTER_ALL) {
    sp.append("status", params.jobStatus);
  }
  return `${SCHEDULED_JOBS_API_PATH}?${sp.toString()}`;
}

export function CommandAgentScheduledJobsTab({ agentId }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });
  const [currentPage, setCurrentPage] = useState(
    () => parseInt(searchParams.get("page") || "1", 10) || 1
  );
  const [sortBy, setSortBy] = useState<ScheduledJobSortField>(
    DEFAULT_TABLE_SORT_BY_CREATED_AT.sortBy as ScheduledJobSortField
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    DEFAULT_TABLE_SORT_BY_CREATED_AT.sortOrder
  );
  const [executionStatusFilter, setExecutionStatusFilter] = useState<string>(
    SCHEDULED_JOB_FILTER_ALL
  );
  const [jobStatusFilter, setJobStatusFilter] = useState<string>(SCHEDULED_JOB_FILTER_ALL);

  const handleSort = (columnKey: string) => {
    const field = SCHEDULED_JOB_SORT_FIELDS.includes(columnKey as ScheduledJobSortField)
      ? (columnKey as ScheduledJobSortField)
      : (DEFAULT_TABLE_SORT_BY_CREATED_AT.sortBy as ScheduledJobSortField);
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const columns = getScheduledJobsColumns({
    sortBy,
    sortOrder,
    handleSort,
  });

  const { data, isLoading, error } = useProtectedQuery<ScheduledJobsListResponse>({
    queryKey: [
      "scheduled-jobs",
      "command-agent",
      agentId,
      currentPage,
      debouncedSearch,
      sortBy,
      sortOrder,
      executionStatusFilter,
      jobStatusFilter,
    ],
    queryFn: () =>
      fetchData(
        buildCommandAgentScheduledJobsUrl({
          agentId,
          page: currentPage,
          limit: DEFAULT_TABLE_PAGE_LIMIT,
          sortBy,
          sortOrder,
          search: debouncedSearch,
          executionStatus: executionStatusFilter,
          jobStatus: jobStatusFilter,
        })
      ),
    enabled: Boolean(agentId),
    requiredPermissions: [],
  });

  useEffect(() => {
    if (data && currentPage > data.page_count) {
      setCurrentPage(1);
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", "1");
      router.replace(`${window.location.pathname}?${params.toString()}`);
    }
  }, [data, currentPage, router, searchParams]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">Scheduled jobs</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Scheduled message runs and jobs for this command agent. Entity scope is this
          agent only.
        </p>
      </div>

      <div className="flex flex-col justify-between gap-3 lg:flex-row">
        <div className="relative w-full lg:max-w-[280px]">
          <Input
            className="h-9 w-full pl-8"
            placeholder="Search scheduled jobs"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            startIcon={<Search className="h-3.5 w-3.5 text-muted-foreground" />}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="min-w-[160px] w-full sm:w-auto lg:min-w-[180px]">
            <Select
              value={executionStatusFilter}
              onValueChange={(v) => {
                setExecutionStatusFilter(v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-full bg-background text-xs">
                <SelectValue placeholder="Execution status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SCHEDULED_JOB_FILTER_ALL}>
                  All execution statuses
                </SelectItem>
                {SCHEDULED_JOB_EXECUTION_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px] w-full sm:w-auto lg:min-w-[180px]">
            <Select
              value={jobStatusFilter}
              onValueChange={(v) => {
                setJobStatusFilter(v);
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-full bg-background text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SCHEDULED_JOB_FILTER_ALL}>
                  All statuses
                </SelectItem>
                {SCHEDULED_JOB_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Failed to load scheduled jobs."}
        </p>
      ) : null}

      <TooltipProvider delayDuration={400}>
        <div className="relative overflow-hidden rounded-lg border">
          {isLoading && <Loader />}
          <div className="max-h-[min(60vh,520px)] min-h-0 overflow-auto">
            <div className="min-w-[900px]">
              <DataTable data={data?.items ?? []} columns={columns} />
            </div>
          </div>
        </div>
      </TooltipProvider>

      <TablePagination
        currentPage={currentPage}
        pageCount={data?.page_count || 0}
        onPageChange={setCurrentPage}
        pageParamName="page"
      />
    </div>
  );
}
